import { TextInput } from '@acorns/grove'
import { useAnalyticsClient, useRemoteStrings } from '@acorns/web-utils'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { SelectionList } from 'components/SelectionList'
import { PageLayout, SecurityFootnote, Button } from 'components/templates'
import {
  trackRegistrationPortfolioQuestionsBackButtonTappedPayload,
  trackRegistrationPortfolioQuestionsNextButtonTappedPayload,
  trackRegistrationPortfolioQuestionsScreenViewedPayload,
  trackLaterRegistrationInvestorQuestionsQuestionViewedPayload,
  trackLaterRegistrationIraRecommendationButtonTappedPayload,
} from 'generated/trackingEvents'
import { PageName } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import { translateEventProps } from 'utils/analytics'
import { withFlowRouter, WithFlowRouterProps } from 'utils/FlowProvider'
import type { StateInput } from 'utils/FlowProvider/requirementsMapping'
import { FlowRoute } from 'utils/FlowProvider/types'

import { getAvailableAnswers } from './availableAnswersText'
import { getPathToMetadata } from './pathToMetadata'
import { removeUnnecessaryRequirements } from './requirements'
import { ProgressBarContainer } from './styled-components'

export const ANSWER_SESSION_KEY = 'SUITABILITY_ANSWERS'

type EnhancedProps = WithFlowRouterProps

const Component = (props: EnhancedProps) => {
  const { state: locationState, pathname } = useLocation<StateInput>()
  const { getRemoteString } = useRemoteStrings()
  const analytics = useAnalyticsClient()
  const [selectedAnswer, setSelectedAnswer] = useState('')
  const pathToMetadata = getPathToMetadata(props.flow)
  const ctaTitle = getRemoteString({ key: 'global.continue' })
  const remoteStringPrefix = pathToMetadata[pathname].stringPrefix
  const isIncomeInput =
    pathname === FlowRoute.AnnualIncome ||
    pathname === FlowRoute.CombinedAnnualIncome ||
    pathname === FlowRoute.SelfEmployedIncome
  const availableAnswers = getAvailableAnswers({
    getRemoteString,
    stringPrefix: remoteStringPrefix,
    pathname: pathname as FlowRoute,
    flow: props.flow,
  })
  const [screenViewedEventName, screenViewedEventPayload] =
    props.flow === 'investOnboarding'
      ? trackRegistrationPortfolioQuestionsScreenViewedPayload({
          type: pathToMetadata[pathname].eventType,
          tier: undefined,
          funnel: undefined,
        })
      : trackLaterRegistrationInvestorQuestionsQuestionViewedPayload({
          containerName: pathToMetadata[pathname].eventType,
          origin: undefined,
        })

  // We increment the requirement count for invest onboarding because the kids question is counted
  // as a suitability question
  let totalNumRequirements =
    locationState.totalNumRequirements ?? locationState.allOnboardingRequirements.length
  props.flow === 'investOnboarding' &&
    locationState.totalNumRequirements === undefined &&
    totalNumRequirements++
  const numRequirementsLeft =
    props.flow === 'investOnboarding'
      ? locationState.allOnboardingRequirements.length + 1
      : locationState.allOnboardingRequirements.length
  const percentageRequirementsLeft = (numRequirementsLeft / totalNumRequirements) * 100

  const handleNextPage = () => {
    const allSuitabilityAnswers = JSON.parse(window.sessionStorage.getItem(ANSWER_SESSION_KEY))
    const newSuitabilityAnswers = {
      ...allSuitabilityAnswers,
      [pathToMetadata[pathname].key]: !isIncomeInput
        ? selectedAnswer
        : Number(selectedAnswer.replace(/\D/g, '')),
    }
    window.sessionStorage.setItem(ANSWER_SESSION_KEY, JSON.stringify(newSuitabilityAnswers))

    const currentRequirementIndex = locationState.allOnboardingRequirements.indexOf(
      pathToMetadata[pathname].requirement,
    )
    locationState.allOnboardingRequirements.splice(currentRequirementIndex, 1)
    const state = {
      allOnboardingRequirements: removeUnnecessaryRequirements({
        allOnboardingRequirements: locationState.allOnboardingRequirements,
        suitability: newSuitabilityAnswers,
        flow: props.flow,
      }),
      totalNumRequirements,
    }

    const routeConfig = {
      from: pathname as FlowRoute,
      action: 'continue',
      state,
      locationState: state,
    }

    const destination = props.retrieveRouteDestination(routeConfig)
    if (props.flow === 'investOnboarding') {
      const [eventName, eventPayload] = trackRegistrationPortfolioQuestionsNextButtonTappedPayload({
        ctaTitle,
        destination: pathToMetadata[destination]?.eventType ?? 'early',
        state: selectedAnswer,
        type: pathToMetadata[pathname].eventType,
        funnel: undefined,
      })
      analytics.track(eventName, translateEventProps(eventPayload))
    } else {
      const [eventName, eventPayload] = trackLaterRegistrationIraRecommendationButtonTappedPayload({
        screen: 'laterRegistrationInvestorQuestions',
        destination: pathToMetadata[destination]?.eventType ?? destination,
        type: pathToMetadata[pathname].eventType,
      })
      analytics.track(eventName, translateEventProps(eventPayload))
    }

    // all suitability questions use the same component during route
    // need to reset the selected answer or the answer will prepopulate the next question
    setSelectedAnswer('')
    props.route(routeConfig)
  }

  const onBackClicked = () => {
    const destination = pathToMetadata[locationState.referrer]
    const [eventName, eventPayload] = trackRegistrationPortfolioQuestionsBackButtonTappedPayload({
      ...(destination && { destination: destination.eventType }),
      type: pathToMetadata[pathname].eventType,
      funnel: undefined,
    })
    analytics.track(eventName, translateEventProps(eventPayload))
  }

  const onAnnualIncomeChange = (e: ChangeEvent<HTMLInputElement>) => {
    // remove any leading zeroes or non-digit characters
    const value = e.target.value.replace(/^0*/, '').replace(/\D/g, '')
    // prepend with dollar sign and add commas
    const formatted = '$' + value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    setSelectedAnswer(formatted)
  }

  useEffect(() => {
    analytics.track(screenViewedEventName, translateEventProps(screenViewedEventPayload))
  }, [pathname])

  return (
    <PageLayout
      eventObjectName={screenViewedEventPayload['object_name']}
      pageName={PageName.SUITABILITY_QUESTIONS}
      headlineText={getRemoteString({
        key: `registration.suitability.question.${remoteStringPrefix}.text`,
      })}
      subtitleText={
        getRemoteString({
          key: `registration.suitability.question.${remoteStringPrefix}.description`,
        }) || null
      }
      navigationBar={{
        centralComponent: (
          <ProgressBarContainer
            data-testid={TestIDs.SUITABILITY_QUESTIONS.PROGRESS_BAR}
            percentage={percentageRequirementsLeft}
          />
        ),
        onBackClicked,
        showBackButton: true,
      }}
      footer={<SecurityFootnote />}
      button={
        <Button
          onClick={handleNextPage}
          testId={TestIDs.SUITABILITY_QUESTIONS.SUBMIT_CTA}
          disabled={selectedAnswer === ''}
        >
          {ctaTitle}
        </Button>
      }
    >
      {!isIncomeInput ? (
        <SelectionList
          options={availableAnswers}
          selectedOptions={{ ...(selectedAnswer && { [selectedAnswer]: true }) }}
          onSelectorClick={(option) => setSelectedAnswer(option)}
        />
      ) : (
        <TextInput
          label={getRemoteString({
            key: `registration.suitability.question.${remoteStringPrefix.replace(/_with_self_employed$/, '')}.input_header`,
          })}
          value={selectedAnswer}
          testId={null}
          placeholder={getRemoteString({
            key: 'registration.suitability.question.annual_income.input_placeholder',
          })}
          onChange={onAnnualIncomeChange}
        />
      )}
    </PageLayout>
  )
}

export const SuitabilityQuestion = withFlowRouter<{}>(Component as any)
